import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { useReportContext } from 'lib/context/ReportContext/ReportContext';
import { formatDateToEndOfDay, formatDateToStartOfDay } from 'lib/helpers/getDateRange';

import { TABS } from 'components/practice/BeyondBilling/Reports/lib/constants';
import Accordion from 'components/shared/Accordion/Accordion';
import Button from 'components/shared/Buttons/Button';
import Checkbox from 'components/shared/Checkbox/Checkbox';
import DatePicker from 'components/shared/DatePicker/DatePicker';
import { dateOptions } from 'components/shared/DateRangeSelector/DateRangeSelector';
import Modal from 'components/shared/Modal/Modal';
import Select from 'components/shared/Select/Select';

import { getDefaultReportFilters } from '../lib/defaultFilters';

import CustomReportFilters from './CustomReportFilters';

const CustomReportModal = ({ name, isOpen, onClose }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const { filters, setFilters, customReport, setCustomReport } = useReportContext();
  const [searchParams] = useSearchParams();

  const category = searchParams.get('category') || TABS[0].value;
  const reportKey = searchParams.get('key');

  const { date, ...restFilters } = filters;

  const defaultReportFilters = getDefaultReportFilters(category, reportKey);
  const { date: defaultDate } = defaultReportFilters;

  const [period] = date;

  const [newFilters, setNewFilters] = useState(restFilters);
  const [newCustomReport, setNewCustomReport] = useState(customReport);
  const [newPeriod, setNewPeriod] = useState({
    values: { startDate: period.values.startDate, endDate: period.values.endDate },
    id: period.values.id
  });
  const showDatePicker = newPeriod?.value?.id === 'custom-date-range';

  const currentPeriodValue = {
    ...newPeriod,
    label: dateOptions.find((option) => option.id === newPeriod?.id)?.label
  };

  const onCloseHandler = () => {
    onClose();
    navigate(location.pathname + location.search, {
      replace: true,
      state: { ...location.state, customize: false }
    });
  };

  const onRunReportHandler = () => {
    const newFilter = {
      date: [{ ...defaultDate, values: { ...newPeriod.values, id: newPeriod.id } }],
      ...newFilters
    };
    setFilters(newFilter);
    setCustomReport(newCustomReport);
    onCloseHandler();
  };

  useEffect(() => {
    if (filters) {
      setNewFilters(restFilters);
      setNewPeriod({
        values: { startDate: period.values.startDate, endDate: period.values.endDate },
        id: period.values.id
      });
    }
  }, [filters]);

  const handleCustomReportChange = (key, value) => {
    setNewCustomReport((prevCustomReport) => ({
      ...prevCustomReport,
      [key]: { ...prevCustomReport[key], ...value }
    }));
  };

  const handleReportPeriodChange = (option) =>
    setNewPeriod((prev) => ({
      ...prev,
      label: option.label,
      values: { ...option.value },
      id: option.id
    }));

  const handleReportCustomPeriodChange = ({ startDate, endDate }) => {
    setNewPeriod((prev) => ({
      ...prev,
      values: {
        ...prev?.values,
        startDate: startDate || prev?.values?.startDate,
        endDate: endDate || prev?.values?.endDate
      }
    }));
  };

  return (
    <Modal
      isOpen={isOpen}
      handleClose={onCloseHandler}
      title={`Customize ${name}`}
      bodyClassName="bg-white"
      className="w-[35%] sm:w-[100%] md:w-[60%]"
      slideFromRight
      footer={
        <div className="flex w-full justify-between gap-2">
          <Button text="Cancel" onClick={onCloseHandler} outlined color="neutral" />
          <Button text="Run Report" onClick={onRunReportHandler} />
        </div>
      }>
      <div className="flex flex-col gap-3">
        <Accordion title="General" defaultOpen>
          <div className="flex gap-3">
            <Select
              label="Report period"
              labelClassName="text-primary-700"
              parentClassName="w-40"
              options={dateOptions}
              value={currentPeriodValue}
              onChange={handleReportPeriodChange}
              isClearable={false}
            />
            {showDatePicker && (
              <>
                <DatePicker
                  label="From"
                  labelClassName="text-primary-700"
                  value={newPeriod?.values?.startDate}
                  onChange={(value) =>
                    handleReportCustomPeriodChange({
                      startDate: formatDateToStartOfDay(value),
                      id: 'custom-date-range'
                    })
                  }
                />
                <DatePicker
                  label="To"
                  labelClassName="text-primary-700"
                  value={newPeriod?.values?.endDate}
                  onChange={(value) =>
                    handleReportCustomPeriodChange({
                      endDate: formatDateToEndOfDay(value),
                      id: 'custom-date-range'
                    })
                  }
                />
              </>
            )}
          </div>
        </Accordion>
        <Accordion title="Filters" defaultOpen>
          <CustomReportFilters
            filters={newFilters}
            setFilters={setNewFilters}
            defaultFilters={defaultReportFilters}
            menuPortalTarget={document.body}
          />
        </Accordion>
        <Accordion title="Header" defaultOpen>
          <Checkbox
            label="Show logo"
            isChecked={newCustomReport?.header?.logo}
            labelTextColor="!text-primary-700"
            onChange={(event) => handleCustomReportChange('header', { logo: event.target.checked })}
          />
          <Checkbox
            label="Practice name"
            isChecked={newCustomReport?.header?.practiceName}
            labelTextColor="!text-primary-700"
            onChange={(event) =>
              handleCustomReportChange('header', { practiceName: event.target.checked })
            }
          />
          <Checkbox
            label="Report period"
            isChecked={newCustomReport?.header?.reportPeriod}
            labelTextColor="!text-primary-700"
            onChange={(event) =>
              handleCustomReportChange('header', { reportPeriod: event.target.checked })
            }
          />
          <Checkbox
            label="Report title"
            isChecked={newCustomReport?.header?.reportTitle}
            labelTextColor="!text-primary-700"
            onChange={(event) =>
              handleCustomReportChange('header', { reportTitle: event.target.checked })
            }
          />
        </Accordion>
      </div>
    </Modal>
  );
};

export default CustomReportModal;
